import { useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowRight } from 'lucide-react';

interface PaginationProps {
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  onPageChange: (page: number) => void;
  itemName?: string;
}

export function Pagination({ page, totalPages, total, limit, onPageChange, itemName = 'items' }: PaginationProps) {
  const [goTo, setGoTo] = useState('');

  if (totalPages <= 1) return null;

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 7; // Total slots: first + last + 5 middle

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    // Always show first and last
    const first = 1;
    const last = totalPages;

    // Calculate middle range
    let left = Math.max(2, page - 2);
    let right = Math.min(totalPages - 1, page + 2);

    // Adjust if near edges
    if (page <= 3) {
      left = 2;
      right = Math.min(totalPages - 1, 5);
    } else if (page >= totalPages - 2) {
      left = Math.max(2, totalPages - 4);
      right = totalPages - 1;
    }

    pages.push(first);

    if (left > 2) pages.push('...');
    else if (left === 2) pages.push(2);

    for (let i = Math.max(left, 3); i <= Math.min(right, totalPages - 1); i++) {
      if (!pages.includes(i)) pages.push(i);
    }

    if (right < totalPages - 1) pages.push('...');
    else if (right === totalPages - 1 && !pages.includes(totalPages - 1)) pages.push(totalPages - 1);

    if (!pages.includes(last)) pages.push(last);

    return pages;
  };

  const pages = getPageNumbers();

  const handleGoTo = (e: React.FormEvent) => {
    e.preventDefault();
    const target = parseInt(goTo, 10);
    if (!isNaN(target) && target >= 1 && target <= totalPages) {
      onPageChange(target);
      setGoTo('');
    }
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-border gap-3">
      <p className="text-sm text-text-muted">
        Showing {start} to {end} of {total} {itemName}
      </p>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1">
          <button
            onClick={() => onPageChange(1)}
            disabled={page === 1}
            className="p-1.5 rounded-lg border border-border hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed"
            title="First page"
          >
            <ChevronsLeft size={16} />
          </button>
          <button
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page === 1}
            className="p-1.5 rounded-lg border border-border hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed"
            title="Previous page"
          >
            <ChevronLeft size={16} />
          </button>

          {pages.map((p, idx) => (
            p === '...' ? (
              <span key={`ellipsis-${idx}`} className="px-2 text-sm text-text-muted">...</span>
            ) : (
              <button
                key={p}
                onClick={() => onPageChange(p as number)}
                className={`min-w-[32px] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${
                  page === p
                    ? 'bg-primary text-white'
                    : 'border border-border hover:bg-background text-text'
                }`}
              >
                {p}
              </button>
            )
          ))}

          <button
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="p-1.5 rounded-lg border border-border hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed"
            title="Next page"
          >
            <ChevronRight size={16} />
          </button>
          <button
            onClick={() => onPageChange(totalPages)}
            disabled={page === totalPages}
            className="p-1.5 rounded-lg border border-border hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed"
            title="Last page"
          >
            <ChevronsRight size={16} />
          </button>
        </div>

        {/* Go to page input for long pagination */}
        {totalPages > 7 && (
          <form onSubmit={handleGoTo} className="hidden md:flex items-center gap-1.5 ml-2">
            <span className="text-xs text-text-muted">Go to</span>
            <input
              type="number"
              min={1}
              max={totalPages}
              value={goTo}
              onChange={(e) => setGoTo(e.target.value)}
              placeholder="#"
              className="w-12 px-1.5 py-1 text-sm text-center border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              type="submit"
              disabled={!goTo || parseInt(goTo, 10) < 1 || parseInt(goTo, 10) > totalPages}
              className="p-1 rounded-lg border border-border hover:bg-background disabled:opacity-50 text-text-muted"
            >
              <ArrowRight size={14} />
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
